import { Injectable } from '@angular/core';
import * as amplitude from '@amplitude/analytics-browser';
import { sessionReplayPlugin } from '@amplitude/plugin-session-replay-browser';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AmplitudeService {
  private initialized = false;

  constructor() {
    this.init();
  }

  private init(): void {
    if (typeof window === 'undefined') {
      return;
    }

    const apiKey = environment.amplitudeApiKey;
    if (!apiKey) {
      console.log('[Amplitude] No API key provided, tracking disabled');
      return;
    }

    amplitude.init(apiKey, {
      autocapture: {
        attribution: true,
        pageViews: true,
        sessions: true,
        formInteractions: false,
        fileDownloads: false,
        elementInteractions: false,
        pageUrlEnrichment: false,
        webVitals: false,
      },
    });
    this.initialized = true;
    console.log('[Amplitude] Initialized');

    this.initSessionReplayForUS();
  }

  /**
   * Track an event with optional properties
   */
  trackEvent(eventName: string, properties?: Record<string, unknown>): void {
    console.log('[Amplitude] Event tracked:', eventName, properties);

    if (!this.initialized) {
      return;
    }

    amplitude.track(eventName, properties);
  }

  /**
   * Set user ID for tracking
   */
  setUserId(userId: string | undefined): void {
    console.log('[Amplitude] User id:', userId);

    if (!this.initialized) {
      return;
    }

    amplitude.setUserId(userId);
  }

  /**
   * Set user properties
   */
  setUserProperties(properties: Record<string, unknown>): void {
    console.log('[Amplitude] User properties:', properties);

    if (!this.initialized) {
      return;
    }

    const identify = new amplitude.Identify();
    Object.entries(properties).forEach(([key, value]) => {
      if (value !== undefined) {
        identify.set(key, value as string | number | boolean | string[] | number[]);
      }
    });
    amplitude.identify(identify);
  }

  /**
   * Reset user identity (call on logout)
   * Clears user ID and generates a new device ID
   */
  reset(): void {
    if (!this.initialized) {
      return;
    }

    amplitude.reset();
  }

  private async initSessionReplayForUS(): Promise<void> {
    try {
      const response = await fetch(`${environment.apiUrl}/geo`);

      if (!response.ok) {
        console.warn('[Amplitude] Failed to fetch geo data, skipping Session Replay');
        return;
      }

      const data = await response.json();

      if (data.country === 'US') {
        const sessionReplay = sessionReplayPlugin({
          sampleRate: 1,
        });
        amplitude.add(sessionReplay);
        console.log('[Amplitude] Session Replay enabled for US user');

        this.trackEvent('amplitude_session_replay_enabled')
      } else {
        console.log('[Amplitude] Session Replay skipped - non-US user:', data.country);
      }
    } catch (error) {
      console.error('[Amplitude] Failed to determine location, skipping Session Replay', error);
    }
  }
}
